/* eslint-disable react-refresh/only-export-components */
import { Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import LangLayout from './layouts/LangLayout'
import FeatureGate from './components/system/FeatureGate'
import type { FlagKey } from './config/flags'
import { DEFAULT_LANG } from './config/locales'

const HomePage = lazy(() => import('./pages/HomePage'))
const ServicesPage = lazy(() => import('./pages/ServicesPage'))
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'))
const TrichologyPage = lazy(() => import('./pages/TrichologyPage'))
const TrichologyDetailPage = lazy(() => import('./pages/TrichologyDetailPage'))
const BranchesPage = lazy(() => import('./pages/BranchesPage'))
const SalonDetailPage = lazy(() => import('./pages/SalonDetailPage'))
const MembershipPage = lazy(() => import('./pages/MembershipPage'))
const GiftCardsPage = lazy(() => import('./pages/GiftCardsPage'))
const GiftGuidePage = lazy(() => import('./pages/GiftGuidePage'))
const ShopPage = lazy(() => import('./pages/ShopPage'))
const ProductPage = lazy(() => import('./pages/ProductPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const CareersPage = lazy(() => import('./pages/CareersPage'))
const ContactPage = lazy(() => import('./pages/ContactPage'))
const HairLengthGuidePage = lazy(() => import('./pages/HairLengthGuidePage'))
const FaqPage = lazy(() => import('./pages/FaqPage'))
const CancellationPolicyPage = lazy(() => import('./pages/CancellationPolicyPage'))
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const CookiesPage = lazy(() => import('./pages/CookiesPage'))
const SuccessPage = lazy(() => import('./pages/SuccessPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))

function LazyLoad({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      {children}
    </Suspense>
  )
}

/** Wraps a page in feature-flag gating + lazy boundary. */
function Gated({ flag, children }: { flag: FlagKey; children: React.ReactNode }) {
  return (
    <FeatureGate flag={flag}>
      <LazyLoad>{children}</LazyLoad>
    </FeatureGate>
  )
}

// Exported so tests can mount the same routes via createMemoryRouter without
// pulling in BrowserRouter / window history.
export const routes = [
  {
    path: '/:lang',
    element: <LangLayout />,
    children: [
      { index: true, element: <Gated flag="HOME"><HomePage /></Gated> },
      { path: 'services', element: <Gated flag="SERVICES"><ServicesPage /></Gated> },
      { path: 'services/:slug', element: <Gated flag="SERVICES"><ServiceDetailPage /></Gated> },
      { path: 'trichology', element: <Gated flag="TRICHOLOGY"><TrichologyPage /></Gated> },
      { path: 'trichology/:slug', element: <Gated flag="TRICHOLOGY"><TrichologyDetailPage /></Gated> },
      { path: 'branches', element: <Gated flag="BRANCHES"><BranchesPage /></Gated> },
      { path: 'salon/:id', element: <Gated flag="BRANCHES"><SalonDetailPage /></Gated> },
      { path: 'membership', element: <Gated flag="MEMBERSHIP"><MembershipPage /></Gated> },
      { path: 'gift-cards', element: <Gated flag="GIFT_CARDS"><GiftCardsPage /></Gated> },
      { path: 'gift-guide', element: <Gated flag="GIFT_GUIDE"><GiftGuidePage /></Gated> },
      { path: 'shop', element: <Gated flag="SHOP"><ShopPage /></Gated> },
      { path: 'shop/:handle', element: <Gated flag="SHOP"><ProductPage /></Gated> },
      { path: 'about', element: <Gated flag="ABOUT"><AboutPage /></Gated> },
      { path: 'careers', element: <Gated flag="CAREERS"><CareersPage /></Gated> },
      { path: 'contact', element: <Gated flag="CONTACT"><ContactPage /></Gated> },
      { path: 'hair-length-guide', element: <Gated flag="HAIR_LENGTH_GUIDE"><HairLengthGuidePage /></Gated> },
      { path: 'hair-guide', element: <Navigate to="../hair-length-guide" replace /> },
      { path: 'faq', element: <Gated flag="FAQ"><FaqPage /></Gated> },
      { path: 'cancellation-policy', element: <Gated flag="CANCELLATION_POLICY"><CancellationPolicyPage /></Gated> },
      { path: 'refund-policy', element: <Gated flag="REFUND_POLICY"><RefundPolicyPage /></Gated> },
      { path: 'privacy', element: <Gated flag="PRIVACY"><PrivacyPage /></Gated> },
      { path: 'cookies', element: <Gated flag="COOKIES"><CookiesPage /></Gated> },
      // Newsletter double-opt-in landing. NOT feature-gated: it's the Brevo
      // post-confirmation redirect target and must always resolve.
      { path: 'newsletter/confirmed', element: <LazyLoad><SuccessPage variant="newsletter" /></LazyLoad> },
    ],
  },
  // Bare (non-locale-prefixed) Brevo redirect target. `/newsletter/confirmed`
  // is two segments, so it does NOT match `/:lang` (one segment) and would
  // otherwise fall through to the `*` NotFound. Redirect it to the default
  // locale so Brevo can point at the un-prefixed path.
  { path: '/newsletter/confirmed', element: <Navigate to={`/${DEFAULT_LANG}/newsletter/confirmed`} replace /> },
  { path: '/', element: <Navigate to={`/${DEFAULT_LANG}`} replace /> },
  { path: '*', element: <LazyLoad><NotFoundPage /></LazyLoad> },
]

// NOTE: the browser router instance (createBrowserRouter) is created in
// main.tsx, NOT here. Keeping this module free of createBrowserRouter lets the
// prerender SSR entry (entry-server.tsx) import `routes` without touching
// `document` (createBrowserHistory throws under Node). Tests build their own
// router from `routes` via createMemoryRouter.
